import type { CaptureOptions } from "posthog-js";
import {
  getTrackingParams,
  type TrackingParams,
} from "@/shared/utils/trackingParams";
import { buildGoogleEnhancedConversionHashes } from "./googleEnhancedConversions";

type PostHogClient = (typeof import("posthog-js"))["default"];

let posthogClient: PostHogClient | null = null;
let analyticsInitPromise: Promise<PostHogClient | null> | null = null;
const pendingCalls: Array<(client: PostHogClient) => void> = [];

function flushPendingCalls(client: PostHogClient): void {
  while (pendingCalls.length > 0) {
    const callback = pendingCalls.shift();
    callback?.(client);
  }
}

function withPostHog(callback: (client: PostHogClient) => void): void {
  if (posthogClient) {
    callback(posthogClient);
    return;
  }

  if (analyticsInitPromise) {
    pendingCalls.push(callback);
  }
}

/** Sem interação, quanto esperar depois da inicialização para gravar a sessão. */
export const SESSION_RECORDING_START_DELAY_MS = 10_000;

const SESSION_RECORDING_START_EVENTS = ["pointerdown", "keydown"] as const;

/**
 * Liga a gravação de sessão no primeiro clique, toque ou tecla, ou depois de
 * `SESSION_RECORDING_START_DELAY_MS`, o que vier primeiro (SA-1474).
 *
 * O gravador (`posthog-recorder`, ~67 kB, mais o snapshot do DOM inteiro)
 * subia junto com a tela e disputava a carga: no Lighthouse de Conversas em
 * staging, bloquear só o gravador levou o mobile de 55 para 59 (bloqueio da
 * thread de 204 para 91 ms). Perde-se só o replay dos primeiros segundos da
 * visita, antes da primeira interação.
 * `startSessionRecording()` sem argumento só desfaz o
 * `disable_session_recording`: amostragem, gatilhos e o liga/desliga do
 * projeto continuam valendo.
 */
function scheduleSessionRecordingStart(client: PostHogClient): void {
  let started = false;

  const start = () => {
    if (started) return;
    started = true;
    window.clearTimeout(timer);
    for (const type of SESSION_RECORDING_START_EVENTS) {
      window.removeEventListener(type, start, true);
    }
    client.startSessionRecording();
  };

  const timer = window.setTimeout(start, SESSION_RECORDING_START_DELAY_MS);
  for (const type of SESSION_RECORDING_START_EVENTS) {
    window.addEventListener(type, start, { capture: true, passive: true });
  }
}

export function initializeAnalytics({
  apiKey,
  apiHost,
}: {
  apiKey?: string;
  apiHost?: string;
}): void {
  if (
    typeof window === "undefined" ||
    !apiKey ||
    !apiHost ||
    posthogClient ||
    analyticsInitPromise
  ) {
    return;
  }

  analyticsInitPromise = import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(apiKey, {
        api_host: apiHost,
        defaults: "2025-05-24",
        capture_exceptions: true,
        // SA-1474: a gravação de sessão liga depois da carga (ver
        // `scheduleSessionRecordingStart`); eventos, identify, Web Vitals e
        // erros seguem desde a inicialização.
        disable_session_recording: true,
        // SA-1469: LCP, INP, CLS e FCP de produção como `$web_vitals`, sem
        // depender do liga/desliga do projeto. O PostHog baixa o script
        // depois do init, fora do caminho da primeira pintura.
        capture_performance: { web_vitals: true },
      });

      posthogClient = posthog;
      flushPendingCalls(posthog);
      scheduleSessionRecordingStart(posthog);

      return posthog;
    })
    .catch(() => {
      pendingCalls.length = 0;
      analyticsInitPromise = null;
      return null;
    });
}

/**
 * Split a full name into first and last name.
 * Everything after the first word is considered the last name.
 */
export function splitName(fullName: string): {
  firstName: string;
  lastName: string;
} {
  const trimmed = fullName.trim();
  const spaceIndex = trimmed.indexOf(" ");
  if (spaceIndex === -1) return { firstName: trimmed, lastName: "" };
  return {
    firstName: trimmed.slice(0, spaceIndex),
    lastName: trimmed.slice(spaceIndex + 1).trim(),
  };
}

/** Remove keys whose value is `undefined`, `null` or empty string. */
function stripEmpty(
  obj: Record<string, unknown>
): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== undefined && v !== null && v !== ""
    )
  ) as Record<string, string | number | boolean>;
}

function getStringValue(
  obj: Record<string, string | number | boolean>,
  key: string
): string | undefined {
  const value = obj[key];
  return typeof value === "string" ? value : undefined;
}

async function setGoogleEnhancedConversionProperties(
  properties: Record<string, string | number | boolean>
): Promise<void> {
  const hashes = await buildGoogleEnhancedConversionHashes({
    email: getStringValue(properties, "email"),
    phone: getStringValue(properties, "phone"),
    phone_number: getStringValue(properties, "phone_number"),
    first_name: getStringValue(properties, "first_name"),
    last_name: getStringValue(properties, "last_name"),
  });

  if (Object.keys(hashes).length === 0) return;
  withPostHog((posthog) => {
    posthog.people?.set(hashes);
  });
}

/**
 * Build person properties from the persisted tracking params.
 * Excludes internal fields like `captured_at`.
 */
function buildTrackingProperties(): Record<string, string> {
  const params = getTrackingParams();
  if (!params) return {};

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { captured_at: _, ...rest } = params;
  return stripEmpty(rest) as Record<string, string>;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Identify a user and set initial person properties.
 * Merges the provided properties with any persisted tracking params
 * (UTM, fbclid, gclid).
 *
 * Call this **once** right after signup (or first login).
 */
export async function identifyUser(
  userId: string | number,
  properties?: {
    email?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    phone_number?: string;
  } & Partial<Omit<TrackingParams, "captured_at">>
): Promise<void> {
  withPostHog(async (posthog) => {
    const tracking = buildTrackingProperties();
    const explicit = properties
      ? stripEmpty(properties as Record<string, unknown>)
      : {};

    const merged = { ...tracking, ...explicit };

    if (Object.keys(merged).length > 0) {
      posthog.identify(String(userId), merged);
    } else {
      posthog.identify(String(userId));
    }

    await setGoogleEnhancedConversionProperties(explicit);
  });
}

/**
 * Update person properties for the current identified user.
 * Uses `posthog.people.set` — only sends non-empty values.
 *
 * Call this when new data becomes available (e.g. after onboarding).
 */
export async function setUserProperties(
  properties: Record<string, unknown>
): Promise<void> {
  withPostHog(async (posthog) => {
    const clean = stripEmpty(properties);
    if (Object.keys(clean).length === 0) return;
    posthog.people?.set(clean);

    await setGoogleEnhancedConversionProperties(clean);
  });
}

/**
 * Vincula o usuário atual a uma Company como PostHog Group (SA-1166).
 *
 * Usa `posthog.group("company", <companyDocumentId>, props)` — o padrão
 * canônico do PostHog para B2B multi-tenant. Permite unir, em funis, os eventos
 * client-side (cujo `distinct_id` é o id do usuário) com os server-side (cujo
 * `distinct_id` é `company_<id>`) via o group `company`.
 *
 * Chame após o `identifyUser`, sempre que a company ativa for conhecida. Seguro
 * chamar repetidamente (ex.: quando o usuário troca de company ativa).
 */
export function identifyCompany(
  companyDocumentId: string,
  properties?: Record<string, unknown>
): void {
  if (!companyDocumentId) return;
  withPostHog((posthog) => {
    const clean = properties ? stripEmpty(properties) : {};
    posthog.group("company", String(companyDocumentId), clean);
  });
}

/**
 * Registra super properties — enviadas automaticamente em todo evento
 * client-side subsequente (SA-1160). Só persiste valores não-vazios.
 *
 * Use para dimensões duráveis e transversais, como `onboarding_variant`.
 */
export function registerSuperProperties(
  properties: Record<string, unknown>
): void {
  withPostHog((posthog) => {
    const clean = stripEmpty(properties);
    if (Object.keys(clean).length === 0) return;
    posthog.register(clean);
  });
}

/**
 * Lê de forma síncrona o valor atual de uma feature flag do PostHog (SA-1160).
 * Retorna `undefined` quando o analytics ainda não inicializou ou as flags não
 * carregaram — quem chama deve fornecer um default sensato.
 */
export function getFeatureFlag(flagKey: string): string | boolean | undefined {
  return posthogClient?.getFeatureFlag(flagKey);
}

/**
 * Capture a named event with optional properties.
 *
 * `options` são repassadas ao `posthog.capture` — úteis para eventos disparados
 * logo antes de um redirect de página inteira (ex.: checkout → Stripe), que
 * precisam de `{ send_instantly: true, transport: "sendBeacon" }` pra não serem
 * cancelados pela navegação.
 */
export function capture(
  event: string,
  properties?: Record<string, unknown>,
  options?: CaptureOptions
): void {
  withPostHog((posthog) => {
    posthog.capture(event, properties ?? {}, options);
  });
}

/** Teto da fila de `captureWhenReady`: sem PostHog (dev sem chave), não cresce. */
export const MAX_EARLY_CAPTURES = 20;

/**
 * Como `capture`, mas guarda o evento quando o PostHog ainda não começou a
 * subir (ele inicia no ocioso, até 1,5 s depois da carga). Serve para medidas
 * da própria carga (SA-1469), que podem ficar prontas antes disso. A fila é
 * limitada: num ambiente sem PostHog os eventos nunca saem.
 */
export function captureWhenReady(
  event: string,
  properties?: Record<string, unknown>
): void {
  if (posthogClient || analyticsInitPromise) {
    capture(event, properties);
    return;
  }
  if (pendingCalls.length >= MAX_EARLY_CAPTURES) return;
  pendingCalls.push((posthog) => {
    posthog.capture(event, properties ?? {});
  });
}

/**
 * Reset the PostHog identity (e.g. on logout).
 */
export function resetAnalytics(): void {
  withPostHog((posthog) => {
    posthog.reset();
  });
}
